<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'

import Button from '../ui/Button.vue'
import { useI18n } from 'vue-i18n'
import { useAuth, useUser, SignInButton, SignOutButton, useClerk } from '@clerk/vue'

const { t, locale } = useI18n()
const route = useRoute()
const drawer = ref(false)
const languageMenu = ref(false)
// Create individual refs for each menu
const cateringMenuOpen = ref(false)
const informationMenuOpen = ref(false)
const { isSignedIn, isLoaded } = useAuth();
const { user } = useUser();
const clerk = useClerk();

const handleSignOut = async () => {
  await clerk.value?.signOut();
  window.location.href = '/';
};

const navigationItems = computed(() => [
  { title: t('menu.menuItems.home'), path: '/', icon: 'mdi-home' },
  { title: t('menu.menuItems.tournament'), path: '/tournaments', icon: 'mdi-trophy' },
  { title: t('menu.menuItems.schedule'), path: '/schedule', icon: 'mdi-calendar' },
  // { title: t('menu.menuItems.catering'), path: '/catering', icon: 'mdi-food' },
  {
    title: t('menu.menuItems.catering'),
    icon: 'mdi-food',
    children: [
      // { title: t('menu.menuItems.cateringOrder'), path: '/alterbyte' },
      { title: t('menu.menuItems.cateringInfo'), path: '/catering' }
    ]
  },
  {
    title: t('menu.menuItems.information'),
    icon: 'mdi-information',
    children: [
      { title: t('location.title'), path: '/information/location' },
      { title: t('information.rules.title'), path: '/information/rules' },
      { title: t('information.bring.title'), path: '/information/what-to-bring' },
      { title: t('information.support.title'), path: '/information/support' },
      { title: t('information.tournaments.title'), path: '/information/tournaments' }
    ]
  },
])

const isInformationChildActive = computed(() => {
  const informationItem = navigationItems.value.find(item => item.children)
  return informationItem?.children?.some(child => route.path === child.path) ?? false
})

const languages = [
  { code: 'en', name: 'EN' },
  { code: 'fr', name: 'FR' },
  { code: 'nl', name: 'NL' }
]

const currentLanguage = computed(() => {
  return languages.find(lang => lang.code === locale.value)?.name || 'EN'
})

const changeLanguage = (lang: string) => {
  locale.value = lang
  localStorage.setItem('language', lang)
  window.document.querySelector('html')?.setAttribute('lang', lang)
  languageMenu.value = false
}

</script>

<template>
  <div class="">
        <router-link to="/" class="brand-icon-link">
          <img 
            src="@/assets/logos/brand_icon_element_flag_webp.webp" 
            alt="ALTERLAN" 
            class="brand-icon"
          />
        </router-link>
    <v-app-bar
      class="navbar px-16"
      height="80"
      elevation="0"
    >
      <!-- Logo Section -->


      <!-- Center Navigation Items -->
      <div class="d-none d-md-flex navigation-items">
        <div class="nav-items-container">
          <!-- Home -->
          <Button
            class="nav-item"
            :to="'/'"
            :class="{ 'nav-active': route.path === '/' }"
            icon-start="mdi-home"
            :no-border="true"
            text-color="#fff"
            variant="text"
          >
            {{ t('menu.menuItems.home') }}
          </Button>
          
          <!-- Tournament -->
          <Button
            class="nav-item"
            :to="'/tournaments'"
            :class="{ 'nav-active': route.path === '/tournaments' }"
            icon-start="mdi-trophy"
            :no-border="true"
            text-color="#fff"
            variant="text"
          >
            {{ t('menu.menuItems.tournament') }}
          </Button>
          
          <!-- Schedule -->
          <Button
            class="nav-item"
            :to="'/schedule'"
            :class="{ 'nav-active': route.path === '/schedule' }"
            icon-start="mdi-calendar"
            :no-border="true"
            text-color="#fff"
            variant="text"
          >
            {{ t('menu.menuItems.schedule') }}
          </Button>
          
          <!-- Catering Dropdown -->
          <v-menu v-model="cateringMenuOpen" location="bottom">
            <template v-slot:activator="{ props }">
              <Button
                v-bind="props"
                class="nav-item"
                :class="{ 'nav-active': route.path.includes('/alterbyte') || route.path.includes('/catering') }"
                icon-start="mdi-food"
                :no-border="true"
                text-color="#fff"
                variant="text"
              >
                {{ t('menu.menuItems.catering') }}
              </Button>
            </template>
            <v-list class="dropdown-menu">
              <v-list-item
                :to="'/alterbyte'"
                class="dropdown-item"
              >
                <v-list-item-title>{{ t('menu.menuItems.cateringOrder') }}</v-list-item-title>
              </v-list-item>
              <v-list-item
                :to="'/catering'"
                class="dropdown-item"
              >
                <v-list-item-title>{{ t('menu.menuItems.cateringInfo') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          
          <!-- Information Dropdown -->
          <v-menu v-model="informationMenuOpen" location="bottom">
            <template v-slot:activator="{ props }">
              <Button
                v-bind="props"
                class="nav-item"
                :class="{ 'nav-active': isInformationChildActive }"
                icon-start="mdi-information"
                :no-border="true"
                text-color="#fff"
                variant="text"
              >
                {{ t('menu.menuItems.information') }}
              </Button>
            </template>
            <v-list class="dropdown-menu">
              <v-list-item
                :to="'/information/location'"
                class="dropdown-item"
              >
                <v-list-item-title>{{ t('location.title') }}</v-list-item-title>
              </v-list-item>
              <v-list-item
                :to="'/information/rules'"
                class="dropdown-item"
              >
                <v-list-item-title>{{ t('information.rules.title') }}</v-list-item-title>
              </v-list-item>
              <v-list-item
                :to="'/information/what-to-bring'"
                class="dropdown-item"
              >
                <v-list-item-title>{{ t('information.bring.title') }}</v-list-item-title>
              </v-list-item>
              <v-list-item
                :to="'/information/support'"
                class="dropdown-item"
              >
                <v-list-item-title>{{ t('information.support.title') }}</v-list-item-title>
              </v-list-item>
              <v-list-item
                :to="'/information/tournaments'"
                class="dropdown-item"
              >
                <v-list-item-title>{{ t('information.tournaments.title') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>

      <template v-slot:append>
        <div class="d-flex align-center">
          <v-menu
            v-model="languageMenu"
            :close-on-content-click="true"
            location="bottom"
            class="language-menu"
          >
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                class="language-select mx-2"
                variant="text"
                density="compact"
              >
                <span class="language-text">{{ currentLanguage }}</span>
                <v-icon
                  :color="'#76c4ae'"
                  class="language-icon"
                  :class="{ 'language-icon-open': languageMenu }"
                >
                  mdi-chevron-down
                </v-icon>
              </v-btn>
            </template>

            <v-list class="language-list pa-0">
              <v-list-item
                v-for="lang in languages"
                :key="lang.code"
                @click="changeLanguage(lang.code)"
                class="language-list-item"
                :class="{ 'language-active': locale === lang.code }"
              >
                <v-list-item-title>{{ lang.name }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <div class="ticket-button-container d-none d-md-block">
            <Button v-if="!isSignedIn" class="navTicketButton text-none" href="https://alterlan.be/tickets?utm_source=alterlan&utm_medium=website&utm_content=get-tickets" target="_blank" rel="noopener noreferrer" style="line-height: 10px;">
              {{t('menu.ticketText')}}
            </Button>
            <Button v-else class="navTicketButton text-none" style="line-height: 10px;" @click="handleSignOut">SIGN OUT</Button>
          </div>
          <v-app-bar-nav-icon
            class="d-flex d-md-none menu-icon"
            @click="drawer = !drawer"
          ></v-app-bar-nav-icon>
        </div>
      </template>
    </v-app-bar>

    <!-- Mobile Navigation Drawer -->
    <v-navigation-drawer
      v-model="drawer"
      temporary
      location="right"
      class="nav-drawer"
    >
      <v-list>
        <!-- Home -->
        <v-list-item
          to="/"
          prepend-icon="mdi-home"
          :title="t('menu.menuItems.home')"
          class="mobile-nav-item"
        />
        
        <!-- Tournament -->
        <v-list-item
          to="/tournaments"
          prepend-icon="mdi-trophy"
          :title="t('menu.menuItems.tournament')"
          class="mobile-nav-item"
        />
        
        <!-- Schedule -->
        <v-list-item
          to="/schedule"
          prepend-icon="mdi-calendar"
          :title="t('menu.menuItems.schedule')"
          class="mobile-nav-item"
        />
        
        <!-- Catering Dropdown -->
        <v-list-group
          value="catering"
        >
          <template v-slot:activator="{ props }">
            <v-list-item
              v-bind="props"
              prepend-icon="mdi-food"
              :title="t('menu.menuItems.catering')"
              class="mobile-nav-item"
            />
          </template>
          
          <v-list-item
            to="/alterbyte"
            :title="t('menu.menuItems.cateringOrder')"
            class="mobile-nav-item"
          />
          
          <v-list-item
            to="/catering"
            :title="t('menu.menuItems.cateringInfo')"
            class="mobile-nav-item"
          />
        </v-list-group>
        
        <!-- Information Dropdown -->
        <v-list-group
          value="information"
        >
          <template v-slot:activator="{ props }">
            <v-list-item
              v-bind="props"
              prepend-icon="mdi-information"
              :title="t('menu.menuItems.information')"
              class="mobile-nav-item"
            />
          </template>
          
          <v-list-item
            to="/information/location"
            :title="t('location.title')"
            class="mobile-nav-item"
          />
          
          <v-list-item
            to="/information/rules"
            :title="t('information.rules.title')"
            class="mobile-nav-item"
          />
          
          <v-list-item
            to="/information/what-to-bring"
            :title="t('information.bring.title')"
            class="mobile-nav-item"
          />
          
          <v-list-item
            to="/information/support"
            :title="t('information.support.title')"
            class="mobile-nav-item"
          />
          
          <v-list-item
            to="/information/tournaments"
            :title="t('information.tournaments.title')"
            class="mobile-nav-item"
          />
        </v-list-group>
      </v-list>

      <template v-slot:append>
        <div class="pa-4">
          <div class="ticket-button-container">
            <Button v-if="!isSignedIn" class="text-none w-100" href="https://alterlan.be/tickets?utm_source=alterlan&utm_medium=website&utm_content=get-tickets" target="_blank" rel="noopener noreferrer" style="line-height: 10px;">
              {{t('menu.ticketText')}}
            </Button>
            <Button v-else class="navTicketButton text-none" style="line-height: 10px;" @click="handleSignOut">SIGN OUT</Button>
          </div>
        </div>
        <v-divider class="my-4 border-opacity-25"></v-divider>
        <div class="mobile-footer-links px-4">
          <v-list-item
            v-for="link in ['privacy', 'terms', 'contact']"
            :key="link"
            :to="`/${link === 'privacy' ? 'privacy-policy' : link === 'terms' ? 'terms' : 'contact'}`"
            class="mobile-footer-link"
            density="compact"
          >
            <v-list-item-title v-html="t(`menu.footerLinks.${link}`)"></v-list-item-title>
          </v-list-item>
        </div>
      </template>
    </v-navigation-drawer>
  </div>
</template>

<style scoped>
.navbar {
  background: #414a4a !important;
  backdrop-filter: blur(50px);
  border-bottom: 2px solid #76c4ae;
  position: relative;
  box-shadow: 
    0 4px 15px -1px rgba(118, 196, 174, 0.2),
    0 4px 30px -1px rgba(118, 196, 174, 0.15),
    0 4px 45px -1px rgba(118, 196, 174, 0.1);
  padding-left: 1rem !important;
  padding-right: 2rem !important;
  display: flex;
  justify-content: center;
}

.navbar::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 100%;
  height: 4px;
  background: linear-gradient(to bottom, 
    rgba(118, 196, 174, 0.4) 0%,
    rgba(118, 196, 174, 0.2) 50%,
    transparent 100%
  );
  filter: blur(2px);
}

.navigation-items {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.nav-items-container {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
}

.nav-item {
  white-space: nowrap;
}

.navigation-items :deep(.v-btn) {
  height: 80px;
  display: flex;
  align-items: center;
  border-radius: 0 !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.navigation-items :deep(.v-btn:hover) {
  color: #76c4ae !important;
  background: rgba(118, 196, 174, 0.1);
  border-radius: 0 !important;
}

.navigation-items :deep(.v-btn__overlay) {
  border-radius: 0 !important;
}

.navigation-items :deep(.v-ripple__container) {
  border-radius: 0 !important;
}

.navigation-items :deep(.v-btn::before),
.navigation-items :deep(.v-btn::after) {
  border-radius: 0 !important;
}

.nav-active {
  position: relative;
  color: #76c4ae !important;
  background: rgba(118, 196, 174, 0.1);
  height: 80px;
  border-radius: 0 !important;
}

.nav-active::before,
.nav-active::after {
  border-radius: 0 !important;
}

.nav-active :deep(.v-icon) {
  color: #76c4ae !important;
  animation: iconPulse 2s ease-in-out infinite;
  transform: scale(1.1);
}

.nav-active::after {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  background: rgba(118, 196, 174, 0.05);
  pointer-events: none;
}

.nav-drawer {
  background: #414a4a !important;
  backdrop-filter: blur(10px);
  border-left: 2px solid #76c4ae;
}

.mobile-nav-item {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.mobile-nav-item:hover {
  background: rgba(118, 196, 174, 0.1);
}

.mobile-nav-item.v-list-item--active {
  background: rgba(118, 196, 174, 0.1);
  color: #76c4ae;
}

.mobile-nav-item.v-list-item--active :deep(.v-icon) {
  color: #76c4ae;
  animation: iconPulse 2s ease-in-out infinite;
}

.language-menu {
  min-width: 30px;
  max-width: 60px;
  height: 100%;
  display: flex;
  align-items: center;
}

.language-select {
  background-color: #414a4a;
  min-height: 32px;
  height: 32px;
  padding: 0 8px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.language-select:hover {
  background-color: rgba(118, 196, 174, 0.1);
}

.language-text {
  color: #76c4ae;
  font-size: 0.9rem;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.language-icon {
  color: #76c4ae !important;
  font-size: 1.2rem;
  margin-left: 0.2rem;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.language-icon-open {
  transform: rotate(180deg);
}

.language-list {
  background-color: #414a4a !important;
  border: 1px solid rgba(118, 196, 174, 0.2);
  border-radius: 0;
  min-width: 60px;
  overflow: hidden;
}

.language-list-item {
  min-height: 36px;
  padding: 0 12px;
  cursor: pointer;
}

.language-list-item :deep(.v-list-item-title) {
  color: #76c4ae;
  font-size: 0.9rem;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.language-list-item:hover {
  background-color: rgba(118, 196, 174, 0.1);
}

.language-active {
  background-color: rgba(118, 196, 174, 0.15);
}

.language-active :deep(.v-list-item-title) {
  color: #76c4ae;
}

.menu-icon {
  color: #76c4ae;
  background-color: #414a4a;
}

.brand-icon-link {
  position: fixed;
  width: 80px;
  top: -10px;
  left: 24%;
  z-index: 9999;
  overflow-y: visible !important;
}


.brand-icon {
  position: absolute;
  top: 0;
  z-index: 9999;
  overflow-y: visible !important;
  height: 120px;
  width: auto;
  transition: transform 0.3s ease;
  transform-style: preserve-3d;
  perspective: 1000px;
  transform: translate3d(0, 0, 0) rotateY(15deg);
  animation: waveBanner 10s ease-in-out infinite;
  transform-origin: left;
  filter: drop-shadow(2px 4px 6px rgba(0, 0, 0, 0.3));
}



@keyframes waveBanner {
  0% {
    transform: translate3d(0, 0, 0) rotateY(15deg) rotateZ(0deg);
  }
  25% {
    transform: translate3d(0, 2px, 10px) rotateY(18deg) rotateZ(2deg);
  }
  50% {
    transform: translate3d(0, 5px, -30px) rotateY(15deg) rotateZ(1deg);
  }
  75% {
    transform: translate3d(0, -2px, 10px) rotateY(12deg) rotateZ(-3deg);
  }
  100% {
    transform: translate3d(0, 0, 0) rotateY(15deg) rotateZ(0deg);
  }
}

.brand-icon:hover {
  filter: drop-shadow(4px 8px 12px rgba(0, 0, 0, 0.4));
}

@keyframes waveBannerHover {
  0% {
    transform: translate3d(0, 0, 0) rotateY(15deg) rotateZ(0deg);
  }
  25% {
    transform: translate3d(0, 4px, 20px) rotateY(20deg) rotateZ(2deg);
  }
  50% {
    transform: translate3d(0, 0, 40px) rotateY(15deg) rotateZ(0deg);
  }
  75% {
    transform: translate3d(0, -4px, 20px) rotateY(10deg) rotateZ(-2deg);
  }
  100% {
    transform: translate3d(0, 0, 0) rotateY(15deg) rotateZ(0deg);
  }
}

@media (max-width: 1432px) {
  .navbar {
    padding-left: 0.5rem !important;
    padding-right: 0.5rem !important;
  }

  .navigation-items {
    gap: 0.5rem;
  }

  .navigation-items :deep(.v-btn) {
    padding: 0 0.75rem;
  }

  .px-8 {
    padding-left: 0.5rem !important;
    padding-right: 0.5rem !important;
  }

  :deep(.mobile-logo) {
    transform: scale(0.9);
  }
}

@media (max-width: 1215px) {
  .navigation-items {
    gap: 0.25rem;
  }

  .navigation-items :deep(.v-btn) {
    padding: 0 0.5rem;
    font-size: 0.9rem;
  }

  :deep(.mobile-logo) {
    transform: scale(0.75);
  }

  :deep(.v-btn__content) {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .language-select:deep(.v-btn__content) {
    display: flex;
    flex-direction: row;
    gap: 0.25rem;
  }

  :deep(.v-icon) {
    margin-right: 0 !important;
    margin-bottom: 2px;
  }
}

@media (max-width: 1065px) {
  .navbar {
    padding-left: 0.5rem !important;
    padding-right: 0.5rem !important;
  }

  .navTicketButton {
    width: 100%;
    font-size: 0.6rem;
  }

  .navTicketButton :deep(.v-btn__content) {
    line-height: 10px !important;
  }

  .nav-item {
    font-size: 0.75rem !important;
  }

  .navbar {
    padding-left: 1rem !important;
    padding-right: 1rem !important;
  }
  
  .px-8 {
    padding-left: 0.5rem !important;
    padding-right: 0.5rem !important;
  }

  :deep(.mobile-logo) {
    transform: scale(0.8);
  }
}

@media (max-width: 2430px) {
  .brand-icon-link {
    left: 23%;
  }
}

@media (max-width: 2330px) {
  .brand-icon-link {
    left: 22%;
  }
}

@media (max-width: 2230px) {
  .brand-icon-link {
    left: 21%;
  }
}

@media (max-width: 2150px) {
  .brand-icon-link {
    left: 20%;
  }
}

@media (max-width: 2090px) {
  .brand-icon-link {
    left: 19%;
  }
}

@media (max-width: 2025px) {
  .brand-icon-link {
    left: 18%;
  }
}

@media (max-width: 1960px) {
  .brand-icon-link {
    left: 17%;
  }
}

@media (max-width: 1890px) {
  .brand-icon-link {
    left: 15%;
  }
}

@media (max-width: 1830px) {
  .brand-icon-link {
    left: 13%;
  }
}

@media (max-width: 1770px) {
  .brand-icon-link {
    left: 12%;
  }
}

@media (max-width: 1650px) {
  .brand-icon-link {
    left: 10%;
  }
}

@media (max-width: 1590px) {
  .brand-icon-link {
    left: 9%;
  }
}

@media (max-width: 1530px) {
  .brand-icon-link {
    left: 7%;
  }
}

@media (max-width: 1470px) {
  .brand-icon-link {
    left: 6%;
  }
}

@media (max-width: 1410px) {
  .brand-icon-link {
    left: 5%;
  }
}

@media (max-width: 1350px) {
  .brand-icon-link {
    left: 5%;
  }
}

@media (max-width: 1290px) {
  .brand-icon-link {
    left: 3%;
  }
}

@media (max-width: 1230px) {
  .brand-icon-link {
    left: 3%;
  }
}

@media (max-width: 1170px) {
  .brand-icon-link {
    left: 3%;
  }
}

.ticket-button-container {
  position: relative;
}


@media (max-width: 960px) {
  .ticket-button-container {
    position: relative;
  }

  /* Hide plus button in top navbar on mobile */
  .navbar .ticket-button-container .plus-button {
    display: none;
  }

  /* Show plus button in mobile menu */
  .nav-drawer .ticket-button-container .plus-button {
    display: block;
  }
}

.mobile-footer-links {
  margin-bottom: 1rem;
}

.mobile-footer-link {
  color: white !important;
  font-size: 0.5rem !important;
  opacity: 0.8;
  min-height: 35px !important;
}

.mobile-footer-link div {
  color: white !important;
  font-size: 0.7rem !important;
  opacity: 1;
}

.mobile-footer-link:hover {
  opacity: 1;
}

.dropdown-menu {
  background: #414a4a !important;
  border-bottom: 2px solid #76c4ae;
  margin-top: 0px;
  border-radius: 0 !important;
  box-shadow: 
    0 4px 15px -1px rgba(118, 196, 174, 0.2),
    0 4px 30px -1px rgba(118, 196, 174, 0.15),
    0 4px 45px -1px rgba(118, 196, 174, 0.1);
}

.dropdown-menu::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 100%;
  height: 4px;
  background: linear-gradient(to bottom, 
    rgba(118, 196, 174, 0.4) 0%,
    rgba(118, 196, 174, 0.2) 50%,
    transparent 100%
  );
  filter: blur(2px);
}

.dropdown-item {
  color: #fff;
}

.dropdown-item:hover {
  background: rgba(118, 196, 174, 0.2);
}
</style>
